import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'

import type { Observable } from 'rxjs'

import { ArchiveDocumentTransition } from '@app/constants/constants'
import { ArchiveDocumentForm, ArchiveDocumentSplitPayload } from '@app/interfaces/archive-documents'

import { BaseApiV2 } from '@app/components/generic/utilities'
import { ArchiveDocumentCategoriesService } from '@app/generated/api/archive-document-categories/archive-document-categories.service'
import { ArchiveDocumentTypesService } from '@app/generated/api/archive-document-types/archive-document-types.service'
import { ArchiveDocumentsService } from '@app/generated/api/archive-documents/archive-documents.service'
import type {
  ArchiveDocumentCategoriesListParams,
  ArchiveDocumentDetail,
  ArchiveDocumentTypesListParams,
  ArchiveDocumentsListParams,
  LongRunningJob,
  PaginatedArchiveDocumentCategoryList,
  PaginatedArchiveDocumentListItemList,
  PaginatedArchiveDocumentTypeList,
} from '@app/generated/api/model'

import { ArchiveDocumentUpdatePayload } from './archive-documents.payload'
import { WorkflowApi } from './workflows.api'

@Injectable({
  providedIn: 'root',
})
export class ArchiveDocumentsApi extends BaseApiV2<ArchiveDocumentForm> implements WorkflowApi<ArchiveDocumentDetail, ArchiveDocumentTransition> {
  protected readonly http = inject(HttpClient)
  private readonly archiveDocumentsService = inject(ArchiveDocumentsService)
  private readonly archiveDocumentTypesService = inject(ArchiveDocumentTypesService)
  private readonly archiveDocumentCategoriesService = inject(ArchiveDocumentCategoriesService)
  public readonly baseUrl = 'archive-documents'
  public readonly codeField = 'uuid'

  list (params: ArchiveDocumentsListParams): Observable<PaginatedArchiveDocumentListItemList> {
    return this.archiveDocumentsService.list(params)
  }

  listDocTypes (params: ArchiveDocumentTypesListParams): Observable<PaginatedArchiveDocumentTypeList> {
    return this.archiveDocumentTypesService.list(params)
  }

  listDocCategories (params: ArchiveDocumentCategoriesListParams): Observable<PaginatedArchiveDocumentCategoryList> {
    return this.archiveDocumentCategoriesService.list(params)
  }

  retrieve (code: string): Observable<ArchiveDocumentDetail> {
    return this.archiveDocumentsService.retrieve(code)
  }

  download (code: string) {
    return this.archiveDocumentsService.download(code)
  }

  update (document: ArchiveDocumentForm, payload: ArchiveDocumentUpdatePayload): Observable<ArchiveDocumentDetail> {
    return this.archiveDocumentsService.partialUpdate(document.uuid, payload)
  }

  updateMultiple (documentUuids: string[], payload: ArchiveDocumentUpdatePayload) {
    return this.archiveDocumentsService.updateMultiple({ ...payload, document_uuids: documentUuids })
  }

  delete (document: Pick<ArchiveDocumentForm, 'uuid'>) {
    return this.archiveDocumentsService.destroy(document.uuid)
  }

  deleteMany (documentUuids: string[]) {
    return this.archiveDocumentsService.deleteMultiple({ document_uuids: documentUuids })
  }

  uploadFile (theFile: File, originalUuid?: string): Observable<ArchiveDocumentDetail> {
    return this.archiveDocumentsService.createFromUpload({ theFile, originalUuid })
  }

  split (document: ArchiveDocumentForm, payload: ArchiveDocumentSplitPayload): Observable<ArchiveDocumentDetail> {
    return this.archiveDocumentsService.splitPdf(document.uuid, payload)
  }

  deletePage (document: ArchiveDocumentForm, pageNumber: number): Observable<ArchiveDocumentDetail> {
    return this.archiveDocumentsService.deletePage(document.uuid, { pageNumber })
  }

  export (filters: unknown, _locale: string): Observable<LongRunningJob> {
    return this.archiveDocumentsService._export({ filters })
  }

  play (code: string | number, transition: ArchiveDocumentTransition, extra?: Record<string, unknown>): Observable<ArchiveDocumentDetail> {
    return this.archiveDocumentsService.playTransition(String(code), { transition, extra })
  }
}
